import { isRunning, type PipelineStage, type Review, type ReviewStatus } from "./data/review-data"
import type { Role } from "@/lib/roles"

/**
 * Why a review is not moving, and what may be done about it.
 *
 * A review can be stopped in four ways and they are not the same wait. The
 * order below is the order they are tested, and it is load-bearing:
 *
 *   - `trashed` outranks the rest because the trash changes what acting on the
 *     review means at all, and a trashed review can also be parked or running.
 *   - `needs_input` comes next because it is the only state that never clears
 *     on its own. A question nobody notices is a review that never finishes.
 *   - `stopped` is the consequence of answering that question with `stop`.
 *   - `batch_wait` clears overnight by itself, so it reports rather than asks.
 *
 * A running review is deliberately not one of these. It is progressing, and the
 * findings rail already says so where the reviewer is looking.
 */
export type BlockerKind = "trashed" | "needs_input" | "stopped" | "batch_wait"

export interface Blocker {
  kind: BlockerKind
}

/** Staff decide what a run costs: answering releases it into the judge stages. */
const ANSWERING_ROLES: readonly Role[] = ["admin", "reviewer", "sme"]

/** Everyone but a viewer may file the corrected file, the client included. */
export function canResubmitCorrectedFile(role: Role | undefined): boolean {
  return role !== undefined && role !== "viewer"
}

export function canAnswerUnreadPages(role: Role | undefined): boolean {
  return role !== undefined && ANSWERING_ROLES.includes(role)
}

/**
 * Its own predicate, though it agrees with the one above today.
 *
 * Taking a review off the overnight lane and answering an unread-page question
 * are different decisions about the same money, and folding them into one
 * function would make a future divergence invisible.
 */
export function canRunNow(role: Role | undefined): boolean {
  return role !== undefined && ANSWERING_ROLES.includes(role)
}

/**
 * The one blocker to show, or null when the review is free to be read.
 *
 * Driven by `status` alone, never by whether the note arrived with it. A parked
 * review whose note went missing is still parked, and gating on the note would
 * render no strip, no buttons and no explanation — leaving the one state that
 * cannot clear itself looking like a finished review.
 */
export function blockerFor(review: {
  status: ReviewStatus
  pipelineStage: PipelineStage | null
  trashedAt: string | null
}): Blocker | null {
  if (review.trashedAt !== null) return { kind: "trashed" }
  if (review.status === "needs_input") return { kind: "needs_input" }
  if (review.status === "cancelled") return { kind: "stopped" }
  if (review.pipelineStage === "batch_wait") return { kind: "batch_wait" }
  return null
}

/**
 * Whether a second way to carry on exists.
 *
 * Where the unread pages carry no text at all, including them and leaving them
 * out compare the same thing, so one button is offered and the reason is
 * printed in place of the other. `null` is "not told", which is not "there is
 * none", so only an explicit `false` collapses the choice.
 */
export function hasSecondWayToCarryOn(unreadPagesHaveText: boolean | null): boolean {
  return unreadPagesHaveText !== false
}

/**
 * Whether a page may be sent back through the OCR lane by hand.
 *
 * Three of the server's four refusals are knowable here, so the control is
 * withheld rather than offered and then refused. The fourth — no OCR reader
 * configured — is a property of the deployment, not of the review, which is
 * why pressing it can still fail and the error is surfaced where it is pressed.
 */
export function canReReadPage(
  review: { status: ReviewStatus; trashedAt: string | null },
  role: Role | undefined,
): boolean {
  return (
    canRunNow(role) &&
    !isRunning(review.status) &&
    review.status !== "needs_input" &&
    review.trashedAt === null
  )
}

/** The note, or a sentence that admits we were not told which page. */
export function unreadPagesNote(review: Pick<Review, "ocrNote">): string {
  return (
    review.ocrNote ??
    "A page could not be read. This review did not say which one — open the printed label and the approved master to see."
  )
}
